/* ============================================================
   assets/apex-color.js
   CH2.3 — Canonical color coordination. Tier 2, deferred,
   zero dependencies.

   THE ARCHITECTURAL RULE: JS never declares a color. The server
   (snippets/palette-data.liquid) renders every value as CSS
   variables; this module only re-points the seven --apex-color*
   aliases at a different --apex-cw-{id} entry. No hex map, no
   inline !important styles, no drift, no flash (Finding A dies
   by construction).

   Colorway ids come from DOM data attributes rendered by Liquid:
     - wrapper:        <div class="apex-colorway" data-colorway="{id}">
     - swatch/option:  data-apex-colorway="{id}"  (on input, option,
                       or link element)

   Replaces (retired in CH2.6): color-coordination.js, the
   per-section apx-color-map-{id} JSON blocks, and the inline
   setPrimary() script in sections/main-product.liquid.

   Does NOT touch product-form.js / product-info.js /
   media-gallery.js / product-modal.js — listens only.
   ============================================================ */
(function () {
  'use strict';

  var ALIASES = ['', '-strong', '-soft', '-on-soft', '-contrast', '-complement', '-rgb'];
  var ALIAS_NAMES = {
    '': '--apex-color',
    '-strong': '--apex-color-strong',
    '-soft': '--apex-color-soft',
    '-on-soft': '--apex-on-soft',
    '-contrast': '--apex-color-contrast',
    '-complement': '--apex-color-complement',
    '-rgb': '--apex-color-rgb'
  };

  /* A colorway is valid iff palette-data rendered its base entry. */
  function isKnown(id) {
    if (!id) return false;
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--apex-cw-' + id)
      .trim() !== '';
  }

  /* Point el's aliases at --apex-cw-{id}. Pass null to clear
     (falls back to Tier 0 graphite defaults). */
  function set(el, id) {
    if (!el) return false;
    if (id === null) {
      ALIASES.forEach(function (tier) {
        el.style.removeProperty(ALIAS_NAMES[tier]);
      });
      el.removeAttribute('data-colorway');
      return true;
    }
    if (!isKnown(id)) return false;
    ALIASES.forEach(function (tier) {
      el.style.setProperty(ALIAS_NAMES[tier], 'var(--apex-cw-' + id + tier + ')');
    });
    el.setAttribute('data-colorway', id);
    el.dispatchEvent(new CustomEvent('apex:color', {
      bubbles: true,
      detail: { colorway: id }
    }));
    return true;
  }

  /* Nearest wrapper for an interactive element. */
  function wrapperFor(el) {
    return (el && el.closest && el.closest('.apex-colorway')) ||
      document.querySelector('.apex-colorway') ||
      document.documentElement;
  }

  /* --- init: honor server-rendered wrappers -------------------
     Wrappers with data-colorway but no inline aliases (e.g.
     markup authored before wrapper-style was added) get their
     aliases set here. Wrappers already styled server-side are
     left untouched — first paint stays server-truth. */
  function init() {
    document.querySelectorAll('.apex-colorway[data-colorway]').forEach(function (el) {
      if (!el.style.getPropertyValue('--apex-color')) {
        set(el, el.getAttribute('data-colorway'));
      }
    });
  }

  /* --- variant:change (Dawn) ----------------------------------
     For future multi-variant products. The selected input/option
     must carry data-apex-colorway (rendered by Liquid). */
  document.addEventListener('variant:change', function (e) {
    var scope = (e.target && e.target.querySelectorAll) ? e.target : document;
    var picked =
      scope.querySelector('input[data-apex-colorway]:checked') ||
      scope.querySelector('select [data-apex-colorway]:checked') ||
      null;
    if (picked) set(wrapperFor(e.target), picked.getAttribute('data-apex-colorway'));
  });

  /* --- <select> fallback (dropdown variant pickers) ----------- */
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || el.tagName !== 'SELECT') return;
    var opt = el.options[el.selectedIndex];
    if (opt && opt.hasAttribute('data-apex-colorway')) {
      set(wrapperFor(el), opt.getAttribute('data-apex-colorway'));
    }
  });

  /* --- cross-product swatch preview (optional) ----------------
     Links with data-apex-colorway + data-apex-preview preview
     their colorway on hover/keyboard-focus and restore on
     leave/blur. Keyboard parity per Phase 2 Addendum (WCAG 2.1.1):
     focusin/focusout mirror mouseover/mouseout exactly, so
     Tab-navigation gets the same preview a mouse user gets.
     Purely decorative and non-trapping: focus movement is never
     intercepted. Click-through navigation needs no JS — the
     target PDP renders server-side. */
  function previewStart(e) {
    var link = e.target && e.target.closest && e.target.closest('[data-apex-colorway][data-apex-preview]');
    if (!link) return;
    var wrap = wrapperFor(link);
    if (!wrap.hasAttribute('data-colorway-resting')) {
      wrap.setAttribute('data-colorway-resting', wrap.getAttribute('data-colorway') || '');
    }
    set(wrap, link.getAttribute('data-apex-colorway'));
  }

  function previewEnd(e) {
    var link = e.target && e.target.closest && e.target.closest('[data-apex-colorway][data-apex-preview]');
    if (!link) return;
    var wrap = wrapperFor(link);
    var resting = wrap.getAttribute('data-colorway-resting');
    if (resting !== null) {
      set(wrap, resting === '' ? null : resting);
      wrap.removeAttribute('data-colorway-resting');
    }
  }

  document.addEventListener('mouseover', previewStart);
  document.addEventListener('mouseout', previewEnd);
  document.addEventListener('focusin', previewStart);
  document.addEventListener('focusout', previewEnd);

  /* Public API (successor to window.apexColorManager) */
  window.apexColor = { set: set, isKnown: isKnown };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
